/* eslint-disable @typescript-eslint/no-unsafe-member-access */

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { EditRecurrenceMenu } from '@devexpress/dx-react-scheduler-material-ui';
import React, { useCallback, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import InputLabel from '@components/InputLabel';
import MainTitle from '@components/MainTitle/MainTitle';
import RadioButtonNode from '@components/RadioButtons/RadioButtonNode';
import RadioButtons from '@components/RadioButtons/RadioButtons';
import {
  HorizontalFlex,
  VerticalFlex,
} from '@components/RecordCard/RecordCard.style';
import RoundedButton from '@components/RoundedButton/RoundedButton';

import { useApp } from '@hooks/useApp';

import { LayoutWrapper } from './EditRecurrenceMenu.style';

const messages = defineMessages({
  editing: {
    id: 'editRecurrenceMenuLayout.editing',
    defaultMessage: 'Edit routine',
  },
  deleting: {
    id: 'editRecurrenceMenuLayout.deleting',
    defaultMessage: 'Delete routine',
  },
  current: {
    id: 'editRecurrenceMenuLayout.current',
    defaultMessage: 'This occurrence',
  },
  currentAndFollowing: {
    id: 'editRecurrenceMenuLayout.currentAndFollowing',
    defaultMessage: 'This and following occurrences',
  },
  all: {
    id: 'editRecurrenceMenuLayout.all',
    defaultMessage: 'All occurrences (Entire routine)',
  },
  save: {
    id: 'GLOBAL.save',
    defaultMessage: 'Save',
  },
  delete: {
    id: 'GLOBAL.delete',
    defaultMessage: 'Delete',
  },
  cancel: {
    id: 'GLOBAL.cancel',
    defaultMessage: 'Cancel',
  },
});

/**
 * VITALL styled layout component for the routine scheduler edit recurrence menu popover
 */
const Layout = ({
  availableOperations,
  isDeleting,
  handleClose,
  commit,
}: EditRecurrenceMenu.LayoutProps) => {
  const { formatMessage } = useIntl();
  const { setIsSingleRoutine } = useApp();

  const [currentValue, setCurrentValue] = useState(
    availableOperations[2].value
  );

  React.useEffect(() => {
    setIsSingleRoutine({ isSingleRoutine: currentValue === 'current' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback((value: boolean | number | string) => {
    setCurrentValue(value);
    setIsSingleRoutine({ isSingleRoutine: value === 'current' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCommitButtonClick = () => {
    commit(currentValue);
  };

  return (
    <LayoutWrapper>
      <MainTitle
        title={formatMessage(isDeleting ? messages.deleting : messages.editing)}
      />
      <RadioButtons
        alignment="vertical"
        onChange={handleChange}
        selected={currentValue}
        style={{ paddingLeft: 17 }}
      >
        {availableOperations.map((operation) => {
          let { title } = operation;
          switch (operation.value) {
            case 'current':
              title = formatMessage(messages.current);
              break;
            case 'currentAndFollowing':
              title = formatMessage(messages.currentAndFollowing);
              break;
            case 'all':
            default:
              title = formatMessage(messages.all);
              break;
          }
          return (
            <RadioButtonNode key={operation.value} value={operation.value}>
              <InputLabel label={title} />
            </RadioButtonNode>
          );
        })}
      </RadioButtons>
      <HorizontalFlex justifyContent="center">
        <VerticalFlex alignItems="center" paddingLeft={9}>
          {isDeleting ? (
            <RoundedButton
              autoSize
              height={40}
              onClick={onCommitButtonClick}
              variant="red"
            >
              {formatMessage(messages.delete)}
            </RoundedButton>
          ) : (
            <RoundedButton autoSize height={40} onClick={onCommitButtonClick}>
              {formatMessage(messages.save)}
            </RoundedButton>
          )}
        </VerticalFlex>
        <VerticalFlex alignItems="center" paddingLeft={9}>
          <RoundedButton
            autoSize
            height={40}
            onClick={handleClose}
            variant="grey"
          >
            {formatMessage(messages.cancel)}
          </RoundedButton>
        </VerticalFlex>
      </HorizontalFlex>
    </LayoutWrapper>
  );
};

export default Layout;
